import { computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { pickWeighted } from '@/composables/pickWeighted';
import type { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type {
  CheckoutProductSelection,
  RandomisationDetails,
  RandomisationException,
} from '@/features/Product/types/models';

export const useRandomisation = (
  randomisationDetails: Ref<RandomisationDetails | undefined>,
  exceptionDetails?: Ref<RandomisationException | undefined>,
  otherCartProductHandles?: Ref<string[]>,
) => {
  const { cachedDisplayProducts, cachedCompleteProducts } = storeToRefs(useProductCompleteStore());
  const { completeBaseProducts } = storeToRefs(useProductBaseStore());
  const productSources = computed(() => [
    ...completeBaseProducts.value,
    ...cachedCompleteProducts.value,
    ...cachedDisplayProducts.value,
  ]);

  const availableOptions = computed(() => {
    const details = randomisationDetails.value;
    if (!details?.options?.length) return [];
    return details.options.filter((option) => {
      const product = productSources.value.find((p) => p.attributes.sku === option.sku);
      return product && !product.attributes.soldOut;
    });
  });

  const isRandomisationEnabled = computed(
    () =>
      (!!randomisationDetails.value?.baseSku || !!randomisationDetails.value?.productHandle) &&
      availableOptions.value.length > 0,
  );

  const isExceptionEligible = computed(() => {
    const exception = exceptionDetails?.value;
    const handles = otherCartProductHandles?.value;
    if (!exception || !handles?.length) return false;
    return handles.every((handle) => exception.productHandles.includes(handle));
  });

  const isMysteryItem = (item: CheckoutProductSelection): boolean => {
    const details = randomisationDetails.value;
    if (!details) return false;
    if (details.baseSku && item.sku.startsWith(details.baseSku)) return true;
    if (details.productHandle) {
      return productSources.value.some(
        (p) => p.attributes.sku === item.sku && p.attributes.handle === details.productHandle,
      );
    }
    return false;
  };

  const resolveSkuInBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
  ): CheckoutProductSelection[] => {
    const details = randomisationDetails.value;
    if (!details || (!isRandomisationEnabled.value && !isExceptionEligible.value)) {
      return bundleDetails;
    }
    const selectedCounts = new Map<string, number>();
    return bundleDetails.map((item) => {
      if (!isMysteryItem(item)) return item;
      if (isExceptionEligible.value && exceptionDetails?.value) {
        const { assignedSku } = exceptionDetails.value;
        const resolvedProduct = productSources.value.find(
          (p) => p.attributes.sku === assignedSku.sku,
        );
        if (resolvedProduct) {
          return {
            ...item,
            sku: assignedSku.sku,
            quantity: assignedSku.quantity,
            id: `gid://shopify/ProductVariant/${resolvedProduct.attributes.variantId}`,
          };
        }
      }
      const eligibleOptions = availableOptions.value.filter((option) => {
        const currentCount = selectedCounts.get(option.sku) ?? 0;
        return currentCount < (option.maxQuantity ?? 5);
      });
      const resolvedOption = pickWeighted(eligibleOptions);
      if (!resolvedOption) return item;
      const resolvedProduct = productSources.value.find(
        (p) =>
          p.attributes.sku === resolvedOption.sku &&
          (!details.productHandle || p.attributes.handle === details.productHandle),
      );
      if (!resolvedProduct) return item;
      selectedCounts.set(resolvedOption.sku, (selectedCounts.get(resolvedOption.sku) ?? 0) + 1);
      return {
        ...item,
        sku: resolvedOption.sku,
        id: `gid://shopify/ProductVariant/${resolvedProduct.attributes.variantId}`,
      };
    });
  };

  const resolveRandomisationAtcObject = (items: AddToCartItem[]): AddToCartItem[] => {
    if (!isRandomisationEnabled.value && !isExceptionEligible.value) return items;
    return items.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      if (!customBundleDetails) return item;
      try {
        const resolvedDetails = resolveSkuInBundleDetails(JSON.parse(customBundleDetails));
        return {
          ...item,
          properties: {
            ...item.properties,
            _custom_bundle_details: JSON.stringify(resolvedDetails),
          },
        };
      } catch {
        return item;
      }
    });
  };

  return {
    isRandomisationEnabled,
    availableOptions,
    resolveSkuInBundleDetails,
    resolveRandomisationAtcObject,
  };
};
